import React, { Suspense, useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, Environment, RoundedBox } from "@react-three/drei";

function MedicalCross() {
  const g = useRef();
  useFrame((state) => {
    if (!g.current) return;
    const t = state.clock.getElapsedTime();
    g.current.rotation.y = Math.sin(t * 0.35) * 0.4;
    g.current.rotation.x = Math.cos(t * 0.25) * 0.12;
  });
  const mat = { color: "#1F6FEB", metalness: 0.15, roughness: 0.12, transmission: 0.55, thickness: 1.4, clearcoat: 1, ior: 1.3 };
  return (
    <group ref={g} scale={1.1}>
      <RoundedBox args={[1.15, 3.2, 1.15]} radius={0.16} smoothness={5}>
        <meshPhysicalMaterial {...mat} />
      </RoundedBox>
      <RoundedBox args={[3.2, 1.15, 1.15]} radius={0.16} smoothness={5}>
        <meshPhysicalMaterial {...mat} />
      </RoundedBox>
    </group>
  );
}

function Orb({ position, scale, color }) {
  const ref = useRef();
  useFrame((s) => {
    if (ref.current) ref.current.position.y = position[1] + Math.sin(s.clock.getElapsedTime() + position[0]) * 0.25;
  });
  return (
    <mesh ref={ref} position={position} scale={scale}>
      <sphereGeometry args={[1, 32, 32]} />
      <meshPhysicalMaterial color={color} roughness={0.1} metalness={0.2} transmission={0.4} thickness={1} />
    </mesh>
  );
}

function Scene() {
  const orbs = useMemo(() => ([
    { p: [-3.4, 1.2, -1], s: 0.55, c: "#0D9488" },
    { p: [3.2, -1.4, -1.5], s: 0.7, c: "#A7F3D0" },
    { p: [2.6, 1.8, -2], s: 0.4, c: "#1F6FEB" },
    { p: [-2.8, -1.6, -1], s: 0.35, c: "#38bdf8" },
  ]), []);
  const rig = useRef();
  useFrame((state) => {
    if (!rig.current) return;
    // subtle cursor parallax with damping
    const tx = state.pointer.x * 0.5;
    const ty = state.pointer.y * 0.3;
    rig.current.rotation.y += (tx - rig.current.rotation.y) * 0.05;
    rig.current.rotation.x += (-ty - rig.current.rotation.x) * 0.05;
  });
  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 6, 4]} intensity={1.6} />
      <directionalLight position={[-4, -2, 3]} intensity={0.5} color="#0D9488" />
      <group ref={rig}>
        <Float speed={1.4} rotationIntensity={0.5} floatIntensity={0.9}>
          <MedicalCross />
        </Float>
        {orbs.map((o, i) => (
          <Float key={i} speed={1.2} floatIntensity={1.2}>
            <Orb position={o.p} scale={o.s} color={o.c} />
          </Float>
        ))}
      </group>
      <Environment preset="city" />
    </>
  );
}

const isMobile = () => typeof window !== "undefined" && window.innerWidth < 768;

export default function Hero3D() {
  if (isMobile()) {
    return (
      <div className="w-full h-full flex items-center justify-center" data-testid="hero-3d-fallback">
        <div className="relative w-64 h-64 sm:w-72 sm:h-72">
          <span className="absolute inset-0 rounded-full border border-[#1F6FEB]/15 animate-pulse" />
          <span className="absolute inset-6 rounded-full border border-[#0D9488]/15" />
          <span className="absolute inset-0 rounded-full bg-[#1F6FEB]/[0.05] blur-2xl" />
          <svg viewBox="0 0 100 100" fill="none" className="absolute inset-0 w-full h-full p-10 drop-shadow-[0_16px_30px_rgba(31,111,235,0.25)]">
            <path d="M40 12 h20 v18 h18 v20 h-18 v18 h-20 v-18 h-18 v-20 h18 z" fill="#1F6FEB" />
            <path d="M30 62 C46 58 60 50 78 30" stroke="#0A1128" strokeWidth="4" strokeLinecap="round" fill="none" />
            <path d="M70 24 l10 4 l-4 10" stroke="#0A1128" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
          <span className="absolute -right-1 top-8 w-3 h-3 rounded-full bg-[#0D9488]/70" />
          <span className="absolute left-2 bottom-10 w-2.5 h-2.5 rounded-full bg-[#A7F3D0]" />
          <span className="absolute right-8 bottom-4 w-2 h-2 rounded-full bg-[#38bdf8]" />
        </div>
      </div>
    );
  }
  return (
    <div className="w-full h-full" data-testid="hero-3d-canvas">
      <Canvas camera={{ position: [0, 0, 8], fov: 42 }} dpr={[1, 1.75]} gl={{ antialias: true, alpha: true }}>
        <Suspense fallback={null}>
          <Scene />
        </Suspense>
      </Canvas>
    </div>
  );
}
